import {
  FILE_CHUNK_SIZE,
  SIZE_LIMITS,
  base64UrlToBytes,
  bytesToBase64Url,
  bytesToHex,
  bytesToUtf8,
  utf8ToBytes,
  type Aead,
  type ChannelType,
  type ControlCommand,
  type ControlPayload,
  type DeviceInfo,
  type EncryptedEnvelope,
  type FileChunk,
  type FileMeta,
  type FileOfferPayload,
  type ImagePayload,
  type KeyDistribution,
  type MetaEvent,
  type MetaPayload,
  type PayloadType,
  type TextPayload,
  type WrappedKey,
} from "@zeroclipboard/shared";
import type { DeviceIdentity } from "./crypto/identity";
import {
  aesGcmDecrypt,
  aesGcmEncrypt,
  importAesGcmKey,
  randomBytes,
  sha256,
} from "./crypto/primitives";
import {
  derive1to1SessionKey,
  generateGroupKey,
  unwrapKeyForSelf,
  wrapKeyForDevice,
} from "./crypto/keys";
import { verifyKeyBinding } from "./crypto/pairing";

/** Application-layer handlers invoked after an incoming event is decrypted. */
export interface EngineHandlers {
  onText(payload: TextPayload): void;
  onImage(payload: ImagePayload, bytes: Uint8Array): void;
  /** A file OFFER arrived (metadata only — no bytes yet). Call requestFile()
   *  when the user actually wants it (paste / download). */
  onFileOffer(offer: FileOfferPayload): void;
  /** A peer asked for a file we offered — respond with sendFileData(). Only the
   *  device that made the offer holds the bytes. */
  onFileRequest?(fileId: string): void;
  /** A requested file's bytes fully arrived and passed the integrity check. */
  onFile(meta: FileMeta, bytes: Uint8Array): void;
  onMeta(payload: MetaPayload): void;
  onControl(payload: ControlPayload): void;
  onError?(message: string): void;
}

interface GroupKeyState {
  epoch: number;
  raw: Uint8Array;
  aesKey: CryptoKey;
}

interface EnvelopeOrder {
  timestamp: number;
  deviceId: string;
  seq: number;
}

export interface DecodedFile {
  meta: FileMeta;
  bytes: Uint8Array;
}

/** True when two string sets contain exactly the same elements. */
function sameSet(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

/** Persisted per-device replay state (spec §16): our monotonic send counter and
 *  the highest seq accepted from each peer. Persisting it means a peer's message
 *  can't be replayed across reconnects and our own seq stays strictly rising. */
export interface ReplayState {
  sendSeq: number;
  seen: Record<string, number>;
}

export class ChannelEngine {
  private readonly members = new Map<string, DeviceInfo>();
  private readonly keyBindings = new Map<string, string>();
  private readonly trusted = new Set<string>();
  private readonly sessionKeys = new Map<string, CryptoKey>();
  /** Tombstoned devices (left / revoked) — never re-admitted or re-keyed, even
   *  if the untrusted relay keeps listing them. */
  private readonly removed = new Set<string>();
  /** Recipient set of the most recent key distribution, for change detection. */
  private lastKeyedMembers: Set<string> | null = null;
  private groupKey: GroupKeyState | null = null;
  private seqCounter = 0;
  /** Highest seq accepted per sender device (per-device replay guard, §16). */
  private readonly seqSeen = new Map<string, number>();
  /** File offers received from peers (fileId -> offer), so a completed data
   *  transfer can be matched to its name + integrity hash. */
  private readonly receivedOffers = new Map<string, FileOfferPayload>();
  /** In-flight on-demand file reassembly, keyed by fileId. */
  private readonly fileData = new Map<
    string,
    { count: number; parts: Map<number, Uint8Array> }
  >();
  private lastOrder: EnvelopeOrder | null = null;
  private locked = false;

  constructor(
    readonly channelId: string,
    readonly type: ChannelType,
    private readonly identity: DeviceIdentity,
    private readonly pairingSecret: Uint8Array,
    private readonly handlers: EngineHandlers,
    private readonly replay?: ReplayState,
    private readonly onReplayChange?: () => void
  ) {
    this.trusted.add(identity.deviceId);
    if (replay) {
      this.seqCounter = replay.sendSeq;
      for (const [id, s] of Object.entries(replay.seen))
        this.seqSeen.set(id, s);
    }
  }

  get selfDeviceId(): string {
    return this.identity.deviceId;
  }

  getMembers(): DeviceInfo[] {
    return [...this.members.values()];
  }

  hasSendableKey(): boolean {
    if (this.type === "group") return this.groupKey !== null;
    return this.getPeer() !== null;
  }

  /** True when the channel has been locked by a `lock_channel` control event. */
  get isLocked(): boolean {
    return this.locked;
  }

  /** Sending is permitted only when unlocked AND a channel key is available. */
  canSend(): boolean {
    return !this.locked && this.hasSendableKey();
  }

  isManager(): boolean {
    let smallest: string | null = null;
    for (const id of this.members.keys()) {
      if (smallest === null || id < smallest) smallest = id;
    }
    return smallest === this.identity.deviceId;
  }

  /** Update membership; verify each joiner's key binding END-TO-END. */
  async setMembers(
    members: DeviceInfo[],
    keyBindings?: Record<string, string>
  ): Promise<void> {
    this.members.clear();
    for (const m of members) {
      if (this.removed.has(m.deviceId)) continue; // tombstoned — never re-admit
      this.members.set(m.deviceId, m);
    }
    if (keyBindings) {
      for (const [id, b] of Object.entries(keyBindings)) {
        this.keyBindings.set(id, b);
      }
    }

    // Anchor of trust: earliest member is the creator (its key came via the QR).
    const creatorId = [...this.members.values()].sort(
      (a, b) => a.joinedAt - b.joinedAt || (a.deviceId < b.deviceId ? -1 : 1)
    )[0]?.deviceId;

    for (const m of this.members.values()) {
      if (m.deviceId === this.identity.deviceId) continue;
      if (m.deviceId === creatorId) {
        this.trusted.add(m.deviceId); // trust-on-first-use (pairing anchor)
        continue;
      }
      const binding = this.keyBindings.get(m.deviceId);
      if (!binding) {
        this.trusted.add(m.deviceId); // trust-on-first-use until a binding arrives
        continue;
      }
      const ok = await verifyKeyBinding(
        this.pairingSecret,
        base64UrlToBytes(m.publicKey),
        binding
      );
      if (ok) this.trusted.add(m.deviceId);
      else {
        this.trusted.delete(m.deviceId);
        this.handlers.onError?.(
          `key binding verification FAILED for device ${m.deviceId} — ignoring its key`
        );
      }
    }
  }

  /* ------------------------------ key management ------------------------ */

  /**
   * If we are the group key manager, ensure a GSK exists and is wrapped for all
   * trusted members. The GSK is ROTATED (new epoch) on any membership change so
   * a joiner cannot read pre-join traffic (forward secrecy) and a departed device
   * cannot read post-leave traffic (post-compromise secrecy). Returns a meta
   * envelope + key distribution to push, or null when nothing changed.
   */
  async maybeDistributeKeys(forceRotate = false): Promise<{
    envelope: EncryptedEnvelope;
    keyDistribution: KeyDistribution;
  } | null> {
    if (this.type !== "group" || !this.isManager()) return null;

    // Current recipients: trusted members other than ourselves.
    const recipients = new Set<string>();
    for (const [id] of this.members) {
      if (id === this.identity.deviceId || !this.trusted.has(id)) continue;
      recipients.add(id);
    }
    const membershipChanged =
      this.lastKeyedMembers === null ||
      !sameSet(this.lastKeyedMembers, recipients);

    // No one to key and no forced rotation: keep a key for our own sends, but
    // don't broadcast an empty distribution.
    if (recipients.size === 0 && !forceRotate) {
      if (!this.groupKey) {
        const raw = generateGroupKey();
        this.groupKey = { epoch: 1, raw, aesKey: await importAesGcmKey(raw) };
      }
      this.lastKeyedMembers = recipients;
      return null;
    }

    if (!this.groupKey) {
      const raw = generateGroupKey();
      this.groupKey = { epoch: 1, raw, aesKey: await importAesGcmKey(raw) };
    } else if (forceRotate || membershipChanged) {
      // Rotate: fresh GSK under a new epoch, wrapped only to current members.
      const raw = generateGroupKey();
      this.groupKey = {
        epoch: this.groupKey.epoch + 1,
        raw,
        aesKey: await importAesGcmKey(raw),
      };
    } else {
      return null; // key exists and membership unchanged — nothing to do
    }

    const wraps: Record<string, WrappedKey> = {};
    for (const id of recipients) {
      const info = this.members.get(id)!;
      wraps[id] = await wrapKeyForDevice(
        this.groupKey.raw,
        base64UrlToBytes(info.publicKey)
      );
    }
    this.lastKeyedMembers = recipients;

    const keyDistribution: KeyDistribution = {
      keyEpoch: this.groupKey.epoch,
      wraps,
    };
    const envelope = await this.encryptMeta("sync_complete", "rekey");
    return { envelope, keyDistribution };
  }

  /** Non-manager members recover the current GSK from a distribution. */
  async ingestKeyDistribution(kd: KeyDistribution): Promise<boolean> {
    if (this.type !== "group" || this.isManager()) return false;
    const current = this.groupKey?.epoch ?? 0;
    if (kd.keyEpoch <= current) return false;
    const wrapped = kd.wraps[this.identity.deviceId];
    if (!wrapped) return false;
    try {
      const raw = await unwrapKeyForSelf(
        wrapped,
        this.identity.privateKey,
        this.identity.publicKeyRaw
      );
      this.groupKey = {
        epoch: kd.keyEpoch,
        raw,
        aesKey: await importAesGcmKey(raw),
      };
      return true;
    } catch {
      this.handlers.onError?.("failed to unwrap group key");
      return false;
    }
  }

  /* ------------------------------- encryption --------------------------- */

  async encryptText(
    plain: string,
    html?: string,
    rtf?: string
  ): Promise<EncryptedEnvelope> {
    if (this.locked) throw new Error("channel is locked (send disabled)");
    const bytes = utf8ToBytes(plain);
    if (bytes.length > SIZE_LIMITS.text)
      throw new Error("text exceeds 1 MB limit");
    const payload: TextPayload = {
      type: "text",
      format: html ? "html" : "plain",
      plain,
      ...(html ? { html } : {}),
      ...(rtf ? { rtf } : {}),
    };
    return this.makeEnvelope("text", bytes.length, payload);
  }

  async encryptImage(
    bytes: Uint8Array,
    mimeType: string,
    width?: number,
    height?: number
  ): Promise<EncryptedEnvelope> {
    if (this.locked) throw new Error("channel is locked (send disabled)");
    if (bytes.length > SIZE_LIMITS.image)
      throw new Error("image exceeds 4 MB limit");
    const payload: ImagePayload = {
      type: "image",
      mimeType,
      dataB64: bytesToBase64Standard(bytes),
      ...(width ? { width } : {}),
      ...(height ? { height } : {}),
    };
    return this.makeEnvelope("image", bytes.length, payload);
  }

  /**
   * Announce a file (a "promise"): send metadata only — name, mime, size and an
   * integrity hash — plus a random fileId. NO bytes are uploaded. The caller
   * keeps the file and, on an incoming request for this fileId, streams the
   * bytes with {@link encryptFileData}.
   */
  async encryptFileOffer(
    bytes: Uint8Array,
    filename: string,
    mimeType: string
  ): Promise<{ envelope: EncryptedEnvelope; fileId: string }> {
    if (this.locked) throw new Error("channel is locked (send disabled)");
    if (bytes.length > SIZE_LIMITS.file)
      throw new Error("file exceeds 10 MB limit");
    const fileId = bytesToBase64Url(randomBytes(12));
    const offer: FileOfferPayload = {
      type: "file",
      fileId,
      filename,
      mimeType,
      size: bytes.length,
      hashSha256: bytesToHex(await sha256(bytes)),
    };
    const size = utf8ToBytes(JSON.stringify(offer)).length;
    const envelope = await this.makeEnvelope("file", size, offer);
    return { envelope, fileId };
  }

  /**
   * Stream the actual bytes for a previously-offered file, on demand, split into
   * FILE_CHUNK_SIZE `file-data` chunks. Called only in response to a
   * `request_file` for this fileId.
   */
  async encryptFileData(
    fileId: string,
    bytes: Uint8Array
  ): Promise<EncryptedEnvelope[]> {
    if (this.locked) throw new Error("channel is locked (send disabled)");
    if (bytes.length > SIZE_LIMITS.file)
      throw new Error("file exceeds 10 MB limit");
    const channelKey = await this.getChannelKey();
    const dek = randomBytes(32);
    const dekKey = await importAesGcmKey(dek);
    const count = Math.max(1, Math.ceil(bytes.length / FILE_CHUNK_SIZE));
    const out: EncryptedEnvelope[] = [];
    for (let i = 0; i < count; i++) {
      const slice = bytes.subarray(
        i * FILE_CHUNK_SIZE,
        Math.min((i + 1) * FILE_CHUNK_SIZE, bytes.length)
      );
      const head = this.envelopeHead("file-data", slice.length);
      const aad = envelopeAad(head);
      out.push({
        ...head,
        body: await this.seal(dekKey, slice, aad),
        dek: await this.seal(channelKey, dek, aad),
        chunk: { fileId, index: i, count },
      });
    }
    return out;
  }

  /** Ask the offering device to send a file's bytes (paste / download). */
  async encryptFileRequest(fileId: string): Promise<EncryptedEnvelope> {
    return this.encryptControl("request_file", "channel", { fileId });
  }

  async encryptControl(
    command: ControlCommand,
    target: string = "channel",
    args?: Record<string, string | number | boolean>
  ): Promise<EncryptedEnvelope> {
    const payload: ControlPayload = args
      ? { type: "control", command, target, args }
      : { type: "control", command, target };
    const bytes = utf8ToBytes(JSON.stringify(payload));
    return this.makeEnvelope("control", bytes.length, payload);
  }

  async encryptMeta(
    event: MetaEvent,
    detail?: string
  ): Promise<EncryptedEnvelope> {
    const payload: MetaPayload = {
      type: "meta",
      event,
      deviceId: this.identity.deviceId,
      ...(detail ? { detail } : {}),
    };
    const bytes = utf8ToBytes(JSON.stringify(payload));
    return this.makeEnvelope("meta", bytes.length, payload);
  }

  private async makeEnvelope(
    type: Exclude<PayloadType, "file-data">,
    size: number,
    payload:
      | TextPayload
      | ImagePayload
      | FileOfferPayload
      | MetaPayload
      | ControlPayload
  ): Promise<EncryptedEnvelope> {
    const channelKey = await this.getChannelKey();
    const head = this.envelopeHead(type, size);
    const body = await this.seal(
      channelKey,
      utf8ToBytes(JSON.stringify(payload)),
      envelopeAad(head)
    );
    return { ...head, body };
  }

  /** Build the cleartext routing header. It is also bound as AES-GCM AAD so the
   *  ordering fields (timestamp/seq/deviceId/…) are authenticated: tampering to
   *  defeat the replay/last-write-wins guard breaks decryption. */
  private envelopeHead(
    type: PayloadType,
    size: number
  ): {
    v: 1;
    channelId: string;
    deviceId: string;
    eventId: string;
    type: PayloadType;
    timestamp: number;
    seq: number;
    keyEpoch: number;
    size: number;
  } {
    const seq = ++this.seqCounter;
    if (this.replay) {
      this.replay.sendSeq = this.seqCounter;
      this.onReplayChange?.();
    }
    return {
      v: 1,
      channelId: this.channelId,
      deviceId: this.identity.deviceId,
      eventId: newEventId(),
      type,
      timestamp: Date.now(),
      seq,
      keyEpoch: this.currentEpoch(),
      size,
    };
  }

  /**
   * Apply a control command's local side effects. The initiator calls this
   * because the relay does NOT echo a sender's own events back to it.
   */
  applyLocalControl(payload: ControlPayload): void {
    this.applyControlSideEffects(payload);
  }

  /* ------------------------------- application -------------------------- */

  /** Deterministic event handler. Returns true if the event was applied. */
  async applyIncoming(env: EncryptedEnvelope): Promise<boolean> {
    if (env.deviceId === this.identity.deviceId) return false; // our own echo

    // Per-device replay guard (§16): reject any seq already accepted from this
    // sender. seq is AEAD-authenticated (envelopeAad), so it can't be forged.
    const seen = this.seqSeen.get(env.deviceId);
    if (seen !== undefined && env.seq <= seen) return false;

    // Global last-write-wins ordering (newest content wins for display).
    if (this.lastOrder && !isNewer(env, this.lastOrder)) return false;

    let decoded:
      | TextPayload
      | ImagePayload
      | MetaPayload
      | ControlPayload
      | null;
    let offer: FileOfferPayload | null = null;
    let chunkPart: { chunk: FileChunk; bytes: Uint8Array } | null = null;
    try {
      if (env.type === "file") {
        offer = await this.decryptOffer(env);
        decoded = null;
      } else if (env.type === "file-data") {
        chunkPart = await this.decryptFileChunk(env);
        decoded = null;
      } else {
        decoded = await this.decryptInner(env);
      }
    } catch {
      // Cannot decrypt (e.g. missing key epoch) — do not advance LWW so it can
      // be reprocessed once keys arrive.
      this.handlers.onError?.(`unable to decrypt ${env.type} event`);
      return false;
    }

    this.lastOrder = {
      timestamp: env.timestamp,
      deviceId: env.deviceId,
      seq: env.seq,
    };
    this.seqSeen.set(env.deviceId, env.seq);
    if (this.replay) {
      this.replay.seen[env.deviceId] = env.seq;
      this.onReplayChange?.();
    }

    if (offer) {
      this.receivedOffers.set(offer.fileId, offer);
      this.handlers.onFileOffer(offer);
      return true;
    }
    if (chunkPart) {
      try {
        const done = await this.assembleFileData(chunkPart);
        if (done) this.handlers.onFile(done.meta, done.bytes);
      } catch {
        this.handlers.onError?.("file reassembly failed");
      }
      return true;
    }
    if (!decoded) return false;
    switch (decoded.type) {
      case "text":
        this.handlers.onText(decoded);
        return true;
      case "image":
        this.handlers.onImage(decoded, base64StandardToBytes(decoded.dataB64));
        return true;
      case "meta":
        this.handlers.onMeta(decoded);
        return true;
      case "control":
        this.applyControlSideEffects(decoded);
        this.handlers.onControl(decoded);
        if (decoded.command === "request_file") {
          const fileId = String(decoded.args?.["fileId"] ?? "");
          if (fileId) this.handlers.onFileRequest?.(fileId);
        }
        return true;
      default:
        return false;
    }
  }

  /**
   * Cryptographically-relevant control side effects are applied deterministically
   * before the UI handler runs (spec: control messages are always auto-applied).
   */
  private applyControlSideEffects(p: ControlPayload): void {
    switch (p.command) {
      case "lock_channel":
        this.locked = true;
        break;
      case "unlock_channel":
        this.locked = false;
        break;
      case "revoke_device":
      case "leave_channel":
        if (p.target !== "channel" && p.target !== this.identity.deviceId) {
          this.removed.add(p.target); // tombstone: never re-admit or re-key
          this.members.delete(p.target);
          this.trusted.delete(p.target);
          this.sessionKeys.delete(p.target);
        }
        break;
      default:
        break;
    }
  }

  private async decryptInner(
    env: EncryptedEnvelope
  ): Promise<TextPayload | ImagePayload | MetaPayload | ControlPayload> {
    const key = await this.getChannelKey();
    const plaintext = await this.open(key, env.body, envelopeAad(env));
    return JSON.parse(bytesToUtf8(plaintext));
  }

  private async decryptOffer(
    env: EncryptedEnvelope
  ): Promise<FileOfferPayload> {
    const key = await this.getChannelKey();
    const plaintext = await this.open(key, env.body, envelopeAad(env));
    return JSON.parse(bytesToUtf8(plaintext)) as FileOfferPayload;
  }

  private async decryptFileChunk(
    env: EncryptedEnvelope
  ): Promise<{ chunk: FileChunk; bytes: Uint8Array }> {
    if (!env.dek || !env.chunk) throw new Error("malformed file-data chunk");
    const aad = envelopeAad(env);
    const channelKey = await this.getChannelKey();
    const dek = await this.open(channelKey, env.dek, aad);
    const dekKey = await importAesGcmKey(dek);
    const bytes = await this.open(dekKey, env.body, aad);
    return { chunk: env.chunk, bytes };
  }

  /**
   * Buffer a decrypted on-demand file chunk; once every chunk has arrived,
   * reassemble and integrity-check against the original offer.
   */
  private async assembleFileData(part: {
    chunk: FileChunk;
    bytes: Uint8Array;
  }): Promise<DecodedFile | null> {
    const { fileId, index, count } = part.chunk;
    let entry = this.fileData.get(fileId);
    if (!entry) {
      entry = { count, parts: new Map() };
      this.fileData.set(fileId, entry);
    }
    entry.parts.set(index, part.bytes);
    if (entry.parts.size < entry.count) return null;

    let total = 0;
    for (let i = 0; i < entry.count; i++) {
      const p = entry.parts.get(i);
      if (!p) return null;
      total += p.length;
    }
    const full = new Uint8Array(total);
    let off = 0;
    for (let i = 0; i < entry.count; i++) {
      const p = entry.parts.get(i)!;
      full.set(p, off);
      off += p.length;
    }
    this.fileData.delete(fileId);
    const offer = this.receivedOffers.get(fileId);
    if (!offer) throw new Error("file data arrived without a matching offer");
    if (bytesToHex(await sha256(full)) !== offer.hashSha256)
      throw new Error("file integrity check failed");
    const meta: FileMeta = {
      filename: offer.filename,
      mimeType: offer.mimeType,
      size: offer.size,
      hashSha256: offer.hashSha256,
    };
    return { meta, bytes: full };
  }

  /* --------------------------------- keys ------------------------------- */

  private currentEpoch(): number {
    return this.type === "group" ? (this.groupKey?.epoch ?? 0) : 0;
  }

  private async getChannelKey(): Promise<CryptoKey> {
    if (this.type === "group") {
      if (!this.groupKey) throw new Error("group key not established yet");
      return this.groupKey.aesKey;
    }
    const peer = this.getPeer();
    if (!peer) throw new Error("waiting for the other device to join");
    const cached = this.sessionKeys.get(peer.deviceId);
    if (cached) return cached;
    const raw = await derive1to1SessionKey(
      this.identity.privateKey,
      this.identity.publicKeyRaw,
      base64UrlToBytes(peer.publicKey)
    );
    const key = await importAesGcmKey(raw);
    this.sessionKeys.set(peer.deviceId, key);
    return key;
  }

  private getPeer(): DeviceInfo | null {
    for (const m of this.members.values()) {
      if (
        m.deviceId !== this.identity.deviceId &&
        this.trusted.has(m.deviceId)
      ) {
        return m;
      }
    }
    return null;
  }

  private async seal(
    key: CryptoKey,
    plaintext: Uint8Array,
    aad?: Uint8Array
  ): Promise<Aead> {
    const iv = randomBytes(12);
    const ct = await aesGcmEncrypt(key, iv, plaintext, aad);
    return { nonce: bytesToBase64Url(iv), ciphertext: bytesToBase64Url(ct) };
  }

  private async open(
    key: CryptoKey,
    aead: Aead,
    aad?: Uint8Array
  ): Promise<Uint8Array> {
    return aesGcmDecrypt(
      key,
      base64UrlToBytes(aead.nonce),
      base64UrlToBytes(aead.ciphertext),
      aad
    );
  }
}

/* --------------------------------- helpers ------------------------------- */

function isNewer(env: EncryptedEnvelope, last: EnvelopeOrder): boolean {
  if (env.timestamp !== last.timestamp) return env.timestamp > last.timestamp;
  if (env.deviceId !== last.deviceId) return env.deviceId > last.deviceId;
  return env.seq > last.seq;
}

/** Canonical AEAD associated data binding the cleartext routing header to the
 *  ciphertext, so ordering/replay fields cannot be tampered without breaking
 *  the AES-GCM tag. */
// Associated data that binds an envelope's cleartext header to its ciphertext.
// Exported so the cross-language interop contract can be pinned by a test; it
// MUST stay byte-for-byte identical to the desktop agent's
// `proto::envelope_aad_string` (desktop-agent/src/protocol.cpp).
export function envelopeAad(h: {
  v: number;
  channelId: string;
  deviceId: string;
  eventId: string;
  type: string;
  timestamp: number;
  seq: number;
  keyEpoch: number;
  size: number;
}): Uint8Array {
  return utf8ToBytes(
    `zc-aad|${h.v}|${h.channelId}|${h.deviceId}|${h.eventId}|${h.type}|${h.timestamp}|${h.seq}|${h.keyEpoch}|${h.size}`
  );
}

function newEventId(): string {
  return bytesToBase64Url(randomBytes(12));
}

/** Standard (non-url) base64 for image payloads embedded in JSON. */
function bytesToBase64Standard(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function base64StandardToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}
